import { FC, Dispatch } from "react";
import { FileFullName } from "../../../helper/general/methods";
import type { PdfCompareMode } from "../../../core/pdf/pdf-compare";
import type { FileInterface } from "../../../middleware/interfaces/file/file-interface";
import type { FolderInterface } from "../../../middleware/interfaces/folder/folder-interface";

interface PdfCompareMenuProps {
  comparePanelReveal: boolean;
  compareMode: PdfCompareMode;
  setCompareMode: Dispatch<PdfCompareMode>;
  // "fileID:version" of the PDF compared against, or "" before one is picked
  compareTarget: string;
  setCompareTarget: Dispatch<string>;
  pdfFile: FileInterface | undefined;
  pdfVersion: number;
  projectFiles: FileInterface[];
  projectFolders: FolderInterface[];
  pageNumber: number;
  comparePageCount: number;
  comparePageOffset: number;
  setComparePageOffset: Dispatch<number>;
  compareFade: number;
  setCompareFade: Dispatch<number>;
  // opening the other PDF, or why it could not be opened
  compareStatus: string | null;
}

const compareModes: { mode: PdfCompareMode; label: string; hint: string }[] = [
  {
    mode: "SIDE_BY_SIDE",
    label: "Side by side",
    hint: "The canvas splits. Scrolling and zooming move both sheets together.",
  },
  {
    mode: "OVERLAY",
    label: "Overlay",
    hint: "This sheet in red, the other in blue. Lines on both read dark.",
  },
];

export const CompareTargetValue = (fileID: number, version: number) =>
  `${fileID}:${version}`;

export const PdfCompareMenu: FC<PdfCompareMenuProps> = ({
  comparePanelReveal,
  compareMode,
  setCompareMode,
  compareTarget,
  setCompareTarget,
  pdfFile,
  pdfVersion,
  projectFiles,
  projectFolders,
  pageNumber,
  comparePageCount,
  comparePageOffset,
  setComparePageOffset,
  compareFade,
  setCompareFade,
  compareStatus,
}) => {
  if (!comparePanelReveal) return null;

  // the open file's other versions first, newest at the top
  const otherVersions = pdfFile
    ? Object.keys(pdfFile.fileVersions)
        .map(Number)
        .filter((version) => version !== pdfVersion)
        .sort((first, second) => second - first)
    : [];
  // then every other PDF in the project, at its current version, with its
  // folder so two files of the same name can be told apart
  const otherPdfs = projectFiles
    .filter(
      (file) =>
        file.fileType.toLowerCase() === "pdf" &&
        file.fileID !== pdfFile?.fileID,
    )
    .map((file) => {
      const folder = projectFolders.find(
        ({ folderID }) => folderID === file.fileParentID,
      );
      return {
        value: CompareTargetValue(file.fileID, file.fileActiveVersion),
        label: folder
          ? `${FileFullName(file.fileName, file.fileType)} · ${folder.folderName}`
          : FileFullName(file.fileName, file.fileType),
      };
    })
    .sort((first, second) => first.label.localeCompare(second.label));

  const comparePageNumber = pageNumber + comparePageOffset;

  return (
    <div className="viewerToolPanel">
      <div className="viewerToolPanelHeader">
        <span className="viewerToolPanelLabel">COMPARE</span>
        <span className="viewerToolPanelName">
          Pick a sheet to compare with
        </span>
        <div className="pdfMeasureScale">
          <span>Compare with</span>
          <select
            aria-label="Compare with"
            value={compareTarget}
            onChange={(event) => {
              setCompareTarget(event.target.value);
            }}
          >
            <option value="" disabled>
              Pick a PDF
            </option>
            {otherVersions.length > 0 ? (
              <optgroup label="Other versions of this file">
                {otherVersions.map((version) => (
                  <option
                    key={version}
                    value={CompareTargetValue(pdfFile!.fileID, version)}
                  >
                    Version {version}
                  </option>
                ))}
              </optgroup>
            ) : (
              <></>
            )}
            {otherPdfs.length > 0 ? (
              <optgroup label="Other PDFs in this project">
                {otherPdfs.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>
            ) : (
              <></>
            )}
          </select>
        </div>
        {compareStatus !== null ? (
          <span className="pdfCompareStatus">{compareStatus}</span>
        ) : comparePageCount > 0 ? (
          <label className="pdfCompareStatus pdfComparePage">
            <span>Page {pageNumber} against page</span>
            <input
              type="number"
              className="pdfSearchInput"
              min={1}
              max={comparePageCount}
              value={comparePageNumber}
              onChange={(event) => {
                const page = Number(event.target.value);
                if (page >= 1 && page <= comparePageCount) {
                  setComparePageOffset(page - pageNumber);
                }
              }}
            />
            <span>of {comparePageCount}</span>
          </label>
        ) : (
          <></>
        )}
        {compareMode === "OVERLAY" && comparePageCount > 0 ? (
          <label className="pdfMarkupRange">
            <span>
              {compareFade < 0.5
                ? "Fading the other sheet"
                : compareFade > 0.5
                  ? "Fading this sheet"
                  : "Both sheets in full"}
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={compareFade}
              onChange={(event) => {
                setCompareFade(Number(event.target.value));
              }}
            />
            <span className="pdfCompareFadeEnds">
              <span>This sheet</span>
              <span>Other sheet</span>
            </span>
          </label>
        ) : (
          <></>
        )}
      </div>
      <div className="viewerToolRowWrapper">
        {compareModes.map(({ mode, label, hint }) => (
          <div
            key={mode}
            className={
              mode === compareMode
                ? "viewerToolRow viewerToolRowSelected"
                : "viewerToolRow"
            }
            onClick={() => {
              setCompareMode(mode);
            }}
          >
            <span className="viewerToolRowLabel">{label}</span>
            <span className="viewerToolRowHint">{hint}</span>
          </div>
        ))}
      </div>
      <div className="viewerToolPanelFooter">
        <span>
          Overlay lines the two sheets up by their corners, so it works when
          both are the same size and drawn in the same place on the sheet.
          Closing this panel ends the comparison.
        </span>
      </div>
    </div>
  );
};

export default PdfCompareMenu;
